import { Component } from '@angular/core';
import { slideInAnimation } from './route-animation';
import { PLATFORM_ID, APP_ID, Inject } from '@angular/core';
import * as Sentry from "@sentry/angular";
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import mixpanel from 'mixpanel-browser'
import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { environment } from '../environments/environment';
import { FaroService } from './services/faro.service';
import { SeoService } from './seo/seo.service';

declare let gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    animations: [slideInAnimation],
    standalone: false
})
export class AppComponent {
  title = 'buupass';
  public _opened: boolean = false;

  public _toggleSidebar() {
    this._opened = !this._opened;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(APP_ID) private appId: string,
    private router: Router,
    private swUpdate: SwUpdate,
    private faroService: FaroService,
    seoService: SeoService
  ) {
    seoService.watchRouter();

    if (swUpdate.isEnabled) {
      swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      ).subscribe(() => {
        swUpdate.activateUpdate().then(() => document.location.reload());
      });
    }

    mixpanel.init('663ac35f2cada6880c4af0fb27f8daaa');

    // DEV-154: mirror EVERY Mixpanel event into self-hosted OpenPanel
    // (openpanel.buupass.com) — names snake_cased ('Flight Search' ->
    // flight_search). Mixpanel behavior is untouched (same args, same return);
    // both run in parallel until/unless Mixpanel is retired. New mixpanel.track
    // calls are mirrored automatically, no per-call-site work.
    const mixpanelTrack = mixpanel.track.bind(mixpanel);
    (mixpanel as any).track = (event: string, props?: any, ...rest: any[]) => {
      try {
        const w = window as any;
        if (typeof w.op === 'function') {
          w.op('track', String(event).toLowerCase().replace(/[^a-z0-9]+/g, '_'), props);
        }
      } catch (e) { /* analytics must never break the app */ }
      return mixpanelTrack(event, props, ...rest);
    };

    Sentry.init({
      dsn: "https://d2d3fe00375b83184b26580636a252e3@o1249374.ingest.us.sentry.io/4509310041325569",
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer controlqqq
      tracesSampleRate: 1,
      sendDefaultPii: true,
      integrations: [
        Sentry.replayIntegration()
      ],
      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/flights.buupass\.com\//],
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    });


    if (isPlatformBrowser(this.platformId)) {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          gtag('config', 'G-VVSQ70XN0J', {
            'page_path': event.urlAfterRedirects
          });
          this.pushFunnelEvent(event.urlAfterRedirects);
          this.scrollToTopOnPathChange(event.urlAfterRedirects);
        }
      });
    }

    // Grafana Faro - frontend RUM (errors, web vitals, traces). Additive only,
    // does not replace Mixpanel/Sentry/OpenPanel. Browser-only SDK, guard against SSR.
    if (isPlatformBrowser(this.platformId)) {
      initializeFaro({
        url: environment.faroUrl,
        app: {
          name: environment.faroAppName,
          version: '1.0.0',
          environment: environment.production ? 'production' : 'development',
        },
        sessionTracking: {
          samplingRate: environment.faroSessionSampleRate,
        },
        // Only these 4 backends are actually called from this app
        // (environment.prod.ts). CORS on each explicitly allows
        // traceparent/tracestate (marketplaceapi, paymentsapi,
        // usermanagementapi settings + itinerary's existing permissive
        // config) - verified via live OPTIONS preflight before enabling
        // this, since without that the CORS preflight for any traced
        // request would reject it outright and break the actual API call,
        // not just lose tracing data.
        instrumentations: [
          ...getWebInstrumentations(),
          new TracingInstrumentation({
            instrumentationOptions: {
              propagateTraceHeaderCorsUrls: [
                /^https:\/\/marketplace-v2\.buupass\.com\//,
                /^https:\/\/pay-v2\.buupass\.com\//,
                /^https:\/\/v2-user-management\.buupass\.com\//,
                /^https:\/\/itinerary\.buupass\.com\//,
              ],
            },
          }),
        ],
      });
      this.faroService.markReady();
    }
  }
  

  // Booking funnel event names are shared verbatim with v2.buupass.com (same
  // Faro collector) so both apps' funnels are directly comparable,
  // distinguished by the `app` field Faro already attaches to every event.
  // Verified against actual code, not guessed from route names: /review-and-pay
  // is dead here - grepped the whole src/app tree, zero router.navigate calls
  // to it anywhere, only the route declaration itself - so that entry is
  // removed rather than emitting an event that can never fire. /payments here
  // genuinely combines passenger details AND payment method selection on one
  // page (single paymentForm with setPaymentMethod()), unlike v2.buupass.com
  // which splits those across two pages - so payment_page_viewed stays as-is.
  // no_results_found is NOT here - /nobuses is rendered inline in the results
  // template (booking.component.html), the URL never changes, so a
  // route-pattern match can never fire; it's a state-based event in
  // booking.component.ts's trackNoFlights() instead.
  //
  // PR #132 review (Juliet Wambuku): booking_completed was emitted twice per
  // successful booking - once here on route match, once in SuccessComponent
  // with real decoded booking data (and different attributes, so Faro
  // dedup wouldn't reliably collapse them). Renamed the route-level one to
  // booking_success_viewed (a page-view signal, not conversion) - SuccessComponent's
  // event is the one with verified data. Unlike v2.buupass.com, there's no
  // separate retry-successful route here that would lose its only signal -
  // confirmed via grep, no such route/component exists in this app.
  private static readonly FUNNEL_ROUTES: Array<{ pattern: RegExp; event: string }> = [
    { pattern: /^\/(home)?$/, event: 'search_page_viewed' },
    { pattern: /^\/booking(\/|$)/, event: 'search_results_viewed' },
    { pattern: /^\/payments\//, event: 'payment_page_viewed' },
    { pattern: /^\/(confirm-payments|card\/confirm)\//, event: 'payment_confirmation_viewed' },
    { pattern: /^\/booking-successful\//, event: 'booking_success_viewed' },
  ];

  private pushFunnelEvent(urlAfterRedirects: string): void {
    const path = urlAfterRedirects.split('?')[0];
    const match = AppComponent.FUNNEL_ROUTES.find((r) => r.pattern.test(path));
    if (!match) {
      return;
    }
    this.faroService.pushEvent(match.event, { path });
  }

  private lastPath: string | null = null;

  /**
   * Start every new page at the top. `html, body { overflow-x: hidden }`
   * (assets/css/styles.css) makes <body> the scroll container, so
   * window.scrollTo() and the router's scrollPositionRestoration never move it.
   * Runs on NavigationEnd rather than router-outlet (activate), so it also
   * covers a route page linking to another one (same component, reused).
   * Query-param-only navigations (e.g. the results page date strip) keep their position.
   */
  private scrollToTopOnPathChange(urlAfterRedirects: string): void {
    const path = urlAfterRedirects.split(/[?#]/)[0];
    if (path === this.lastPath) {
      return;
    }
    this.lastPath = path;
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }
}
